import { useCallback, useEffect, useRef, useState } from 'react';
import {
    Alert,
    Box,
    Button,
    CircularProgress,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    FormControl,
    IconButton,
    InputLabel,
    MenuItem,
    Paper,
    Select,
    Stack,
    TextField,
    Tooltip,
    Typography,
} from '@mui/material';
import AddCommentOutlinedIcon from '@mui/icons-material/AddCommentOutlined';
import CallOutlinedIcon from '@mui/icons-material/CallOutlined';
import ChatBubbleOutlineIcon from '@mui/icons-material/ChatBubbleOutline';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import EventOutlinedIcon from '@mui/icons-material/EventOutlined';
import NotesOutlinedIcon from '@mui/icons-material/NotesOutlined';
import {
    createWorkerNote,
    deleteWorkerNote,
    fetchWorkerTimeline,
    SaveWorkerNoteRequest,
    updateWorkerNote,
    WorkerNoteType,
    WorkerTimelineItem,
} from '../../api/workerNotesApi';
import { extractErrorMessageSync } from '../../utils/errorHandler';
import { validateWorkerNoteDuration } from './workerNoteValidation';

const NOTE_LABELS: Record<WorkerNoteType, string> = {
    CALL: 'Call',
    MEETING: 'Meeting',
    WHATSAPP: 'WhatsApp',
    NOTE: 'Note',
};

const EMPTY_NOTE: SaveWorkerNoteRequest = { type: 'NOTE', body: '' };

function itemLabel(item: WorkerTimelineItem): string {
    if (item.kind === 'ADMIN_MESSAGE') return 'Outbound WhatsApp';
    if (item.kind === 'CHAT_MESSAGE') return item.direction === 'OUTGOING' ? 'WhatsApp sent' : 'WhatsApp received';
    return item.noteType ? NOTE_LABELS[item.noteType] : 'Note';
}

function itemIcon(item: WorkerTimelineItem) {
    if (item.kind !== 'NOTE') return <ChatBubbleOutlineIcon fontSize="small" />;
    if (item.noteType === 'CALL') return <CallOutlinedIcon fontSize="small" />;
    if (item.noteType === 'MEETING') return <EventOutlinedIcon fontSize="small" />;
    return <NotesOutlinedIcon fontSize="small" />;
}

export function WorkerContactTimeline({
    workerId,
    canManageWorkers,
}: {
    workerId: string;
    canManageWorkers: boolean;
}) {
    const [items, setItems] = useState<WorkerTimelineItem[]>([]);
    const [nextCursor, setNextCursor] = useState<string | null>(null);
    const [loading, setLoading] = useState(true);
    const [loadingMore, setLoadingMore] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [saveError, setSaveError] = useState<string | null>(null);
    const [editing, setEditing] = useState<WorkerTimelineItem | null>(null);
    const [draft, setDraft] = useState<SaveWorkerNoteRequest>(EMPTY_NOTE);
    const [dialogOpen, setDialogOpen] = useState(false);
    const [saving, setSaving] = useState(false);
    const requestGeneration = useRef(0);
    const actionGeneration = useRef(0);
    const activeWorkerId = useRef(workerId);
    activeWorkerId.current = workerId;
    const durationError = validateWorkerNoteDuration(draft.type, draft.durationMinutes);

    const load = useCallback(async () => {
        if (!canManageWorkers) return;
        if (activeWorkerId.current !== workerId) return;
        const generation = ++requestGeneration.current;
        setLoading(true);
        setError(null);
        try {
            const response = await fetchWorkerTimeline(workerId);
            if (generation !== requestGeneration.current || activeWorkerId.current !== workerId) return;
            setItems(response.items);
            setNextCursor(response.nextCursor);
        } catch (loadError) {
            if (generation !== requestGeneration.current || activeWorkerId.current !== workerId) return;
            setError(extractErrorMessageSync(loadError));
        } finally {
            if (generation === requestGeneration.current && activeWorkerId.current === workerId) setLoading(false);
        }
    }, [canManageWorkers, workerId]);

    useEffect(() => {
        requestGeneration.current += 1;
        setItems([]);
        setNextCursor(null);
        setLoadingMore(false);
        setEditing(null);
        setDraft(EMPTY_NOTE);
        setDialogOpen(false);
        setSaving(false);
        actionGeneration.current += 1;
        void load();
        return () => {
            requestGeneration.current += 1;
        };
    }, [load]);

    const loadMore = async () => {
        if (!canManageWorkers || !nextCursor || loadingMore) return;
        const targetWorkerId = workerId;
        const cursor = nextCursor;
        const generation = ++requestGeneration.current;
        setLoadingMore(true);
        setError(null);
        try {
            const response = await fetchWorkerTimeline(targetWorkerId, cursor);
            if (generation !== requestGeneration.current || activeWorkerId.current !== targetWorkerId) return;
            setItems((current) => appendUniqueTimelineItems(current, response.items));
            setNextCursor(response.nextCursor);
        } catch (loadError) {
            if (generation === requestGeneration.current && activeWorkerId.current === targetWorkerId) {
                setError(extractErrorMessageSync(loadError));
            }
        } finally {
            if (generation === requestGeneration.current && activeWorkerId.current === targetWorkerId) {
                setLoadingMore(false);
            }
        }
    };

    const openCreate = () => {
        if (!canManageWorkers) return;
        setEditing(null);
        setDraft(EMPTY_NOTE);
        setSaveError(null);
        setDialogOpen(true);
    };

    const openEdit = (item: WorkerTimelineItem) => {
        if (!canManageWorkers) return;
        setEditing(item);
        setDraft({
            type: item.noteType ?? 'NOTE',
            body: item.body,
            durationMinutes: item.durationMinutes ?? undefined,
        });
        setSaveError(null);
        setDialogOpen(true);
    };

    const save = async () => {
        if (!canManageWorkers) return;
        const body = draft.body.trim();
        if (!body || durationError) return;
        const targetWorkerId = workerId;
        const generation = actionGeneration.current;
        setSaving(true);
        setSaveError(null);
        try {
            const payload = { ...draft, body };
            if (editing) await updateWorkerNote(targetWorkerId, editing.sourceId, payload);
            else await createWorkerNote(targetWorkerId, payload);
            if (activeWorkerId.current !== targetWorkerId || actionGeneration.current !== generation) return;
            setItems([]);
            setDialogOpen(false);
            await load();
        } catch (saveError) {
            if (activeWorkerId.current === targetWorkerId && actionGeneration.current === generation) {
                setSaveError(extractErrorMessageSync(saveError));
            }
        } finally {
            if (activeWorkerId.current === targetWorkerId && actionGeneration.current === generation) setSaving(false);
        }
    };

    const remove = async (item: WorkerTimelineItem) => {
        if (!canManageWorkers) return;
        if (!window.confirm('Delete this contact note?')) return;
        const targetWorkerId = workerId;
        const generation = actionGeneration.current;
        try {
            await deleteWorkerNote(targetWorkerId, item.sourceId);
            if (activeWorkerId.current !== targetWorkerId || actionGeneration.current !== generation) return;
            setItems([]);
            await load();
        } catch (removeError) {
            if (activeWorkerId.current === targetWorkerId && actionGeneration.current === generation) {
                setError(extractErrorMessageSync(removeError));
            }
        }
    };

    if (!canManageWorkers) return null;

    return (
        <Stack spacing={2}>
            <Stack direction={{ xs: 'column', sm: 'row' }} justifyContent="space-between" spacing={1}>
                <Box>
                    <Typography variant="h6" fontWeight={800}>Contact timeline</Typography>
                    <Typography variant="body2" color="text.secondary">
                        Recruiter notes and WhatsApp contact moments, newest first.
                    </Typography>
                </Box>
                <Button
                    variant="contained"
                    startIcon={<AddCommentOutlinedIcon />}
                    onClick={openCreate}
                    disabled={loading || Boolean(error)}
                >
                    Add contact
                </Button>
            </Stack>

            {error && (
                <Alert
                    severity="error"
                    action={<Button color="inherit" size="small" onClick={() => void load()}>Retry</Button>}
                >
                    {error}
                </Alert>
            )}
            {loading ? (
                <Box sx={{ display: 'grid', placeItems: 'center', minHeight: 160 }}><CircularProgress /></Box>
            ) : !error && items.length === 0 ? (
                <Alert severity="info">No contact moments have been recorded yet.</Alert>
            ) : (
                <Stack spacing={1}>
                    {items.map((item) => (
                        <Paper key={`${item.kind}:${item.sourceId}`} variant="outlined" sx={{ p: 1.5, borderRadius: 1 }}>
                            <Stack direction="row" spacing={1.5} alignItems="flex-start">
                                <Box sx={{ color: 'primary.main', pt: 0.25 }}>{itemIcon(item)}</Box>
                                <Box sx={{ flex: 1, minWidth: 0 }}>
                                    <Stack direction={{ xs: 'column', sm: 'row' }} spacing={0.75} alignItems={{ sm: 'center' }}>
                                        <Typography variant="subtitle2" fontWeight={700}>{itemLabel(item)}</Typography>
                                        <Typography variant="caption" color="text.secondary">
                                            {new Date(item.occurredAt).toLocaleString()}
                                            {item.durationMinutes ? ` / ${item.durationMinutes} min` : ''}
                                        </Typography>
                                    </Stack>
                                    <Typography variant="body2" sx={{ mt: 0.5, whiteSpace: 'pre-wrap', overflowWrap: 'anywhere' }}>
                                        {item.body}
                                    </Typography>
                                    {(item.authorLabel || item.adminUsername) && (
                                        <Typography variant="caption" color="text.secondary">
                                            By {item.authorLabel || item.adminUsername}
                                        </Typography>
                                    )}
                                </Box>
                                {item.kind === 'NOTE' && (
                                    <Stack direction="row">
                                        <Tooltip title="Edit note"><IconButton aria-label="Edit note" size="small" onClick={() => openEdit(item)}><EditOutlinedIcon fontSize="small" /></IconButton></Tooltip>
                                        <Tooltip title="Delete note"><IconButton aria-label="Delete note" size="small" color="error" onClick={() => void remove(item)}><DeleteOutlineIcon fontSize="small" /></IconButton></Tooltip>
                                    </Stack>
                                )}
                            </Stack>
                        </Paper>
                    ))}
                    {nextCursor && (
                        <Box sx={{ display: 'flex', justifyContent: 'center', pt: 1 }}>
                            <Button variant="outlined" onClick={() => void loadMore()} disabled={loadingMore}>
                                {loadingMore ? 'Loading...' : 'Load more'}
                            </Button>
                        </Box>
                    )}
                </Stack>
            )}

            <Dialog open={dialogOpen && canManageWorkers} onClose={() => !saving && setDialogOpen(false)} fullWidth maxWidth="sm">
                <DialogTitle>{editing ? 'Edit contact moment' : 'Add contact moment'}</DialogTitle>
                <DialogContent>
                    <Stack spacing={2} sx={{ pt: 1 }}>
                        {saveError && <Alert severity="error">{saveError}</Alert>}
                        <FormControl size="small" fullWidth>
                            <InputLabel id="worker-note-type-label">Type</InputLabel>
                            <Select id="worker-note-type" labelId="worker-note-type-label" value={draft.type} label="Type" onChange={(event) => {
                                const type = event.target.value as WorkerNoteType;
                                setDraft({ ...draft, type, durationMinutes: type === 'CALL' || type === 'MEETING' ? draft.durationMinutes : null });
                            }}>
                                {Object.entries(NOTE_LABELS).map(([value, label]) => <MenuItem key={value} value={value}>{label}</MenuItem>)}
                            </Select>
                        </FormControl>
                        {(draft.type === 'CALL' || draft.type === 'MEETING') && (
                            <TextField
                                label="Duration (minutes)"
                                type="number"
                                size="small"
                                inputProps={{ min: 1, max: 1440, step: 1 }}
                                error={Boolean(durationError)}
                                helperText={durationError}
                                value={draft.durationMinutes ?? ''}
                                onChange={(event) => setDraft({ ...draft, durationMinutes: event.target.value ? Number(event.target.value) : null })}
                            />
                        )}
                        <TextField label="Notes" multiline minRows={4} value={draft.body} inputProps={{ maxLength: 4000 }} onChange={(event) => setDraft({ ...draft, body: event.target.value })} />
                    </Stack>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setDialogOpen(false)} disabled={saving}>Cancel</Button>
                    <Button variant="contained" onClick={() => void save()} disabled={saving || !draft.body.trim() || Boolean(durationError)}>
                        {saving ? 'Saving...' : 'Save'}
                    </Button>
                </DialogActions>
            </Dialog>
        </Stack>
    );
}

function appendUniqueTimelineItems(
    current: WorkerTimelineItem[],
    older: WorkerTimelineItem[],
): WorkerTimelineItem[] {
    const seen = new Set(current.map((item) => `${item.kind}:${item.sourceId}`));
    return [...current, ...older.filter((item) => {
        const key = `${item.kind}:${item.sourceId}`;
        if (seen.has(key)) return false;
        seen.add(key);
        return true;
    })];
}
